// @flow
import React from 'react';
import {Helmet} from 'react-helmet';

import {useSiteMetadata} from '../hooks/useSiteMetadata';
import {PageMetaType, PathType} from '../types';

export default function PageMeta({path, pageMeta}) {
  const siteMetadata = useSiteMetadata();
  const canonical = siteMetadata.gatsby.siteUrl + path; // Builds the full canonical from the path prop, as: https://hanno.co/page/name/here/

  const renderMetaObjectAsTags = () => {
    if (pageMeta && pageMeta.dato) {
      // The pageMeta.dato object is returned by DatoCMS and contains a full object of all tags we should output for this post
      const title = !pageMeta.dato[0].attributes && pageMeta.dato[0].content; // The first item inside the DatoCMS pageMeta object has no attributes and represents the entry title
      return (
        <Helmet title={title}>
          {pageMeta.dato.map((d, idx) => {
            const {attributes} = d;

            if (attributes) {
              if (attributes.name) {
                return (
                  <meta
                    key={idx}
                    name={attributes.name}
                    content={attributes.content}
                  />
                );
              } else if (attributes.property) {
                return (
                  <meta
                    key={idx}
                    property={attributes.property}
                    content={attributes.content}
                  />
                );
              }
            }
          })}
        </Helmet>
      );
    } else if (pageMeta && pageMeta.strings) {
      // A meta object has been passed into the layout from the single view
      const {strings} = pageMeta;
      const title = strings.title ? strings.title : siteMetadata.gatsby.title; // Because title is not always required, such as on the homepage
      return (
        <Helmet title={strings.title}>
          <meta property="og:title" content={title} />
          <meta name="twitter:title" content={title} />
          <meta name="description" content={strings.description} />
          <meta property="og:description" content={strings.description} />
          <meta name="twitter:description" content={strings.description} />
        </Helmet>
      );
    }
    // Fallback in case we have neither pageMeta.strings or pageMeta.dato
    // Technically we would be able to define this inside the regular Helmet and it would be overriden, but by including it inside this function,
    // we can be sure that it will not be output unless we want it to be
    return (
      <Helmet>
        <meta property="og:title" content={siteMetadata.gatsby.title} />
        <meta name="twitter:title" content={siteMetadata.gatsby.title} />
        <meta
          name="description"
          content={siteMetadata.dato.fallbackSeo.description}
        />
        <meta
          property="og:description"
          content={siteMetadata.dato.fallbackSeo.description}
        />
        <meta
          name="twitter:description"
          content={siteMetadata.dato.fallbackSeo.description}
        />
      </Helmet>
    );
  };

  return (
    <React.Fragment>
      {/* Global items - will apply unless a tag is overriden in another Helmet include */}
      {/* E.g. when a Dato single post exists, it will re-declare all of the social sharing images, thus it's not a problem that they are defined here */}
      <Helmet
        bodyAttributes={{
          class: path === '/' ? 'backgroundOffwhite' : 'backgroundDefault',
        }}
        defaultTitle={siteMetadata.dato.siteName}
        titleTemplate={`%s - ${siteMetadata.dato.siteName}`}>
        <html lang="en" />
        {/* Canonicals - very important! */}
        <link rel="canonical" href={canonical} />
        <meta property="og:url" content={canonical} />
        {/* Global settings which are not provided by DatoCMS */}
        <meta property="og:locale" content="en" />
        <meta name="Copyright" content="Copyright (c) Hanno Limited" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,shrink-to-fit=no,viewport-fit=cover"
        />
        <meta name="theme-color" content="#ef4b3b" />
        <meta name="msapplication-navbutton-color" content="#ef4b3b" />
        <meta name="apple-mobile-web-app-status-bar-style" content="#ef4b3b" />
        <meta name="twitter:creator" content="Hanno" />

        {/* Build the favicon tags based on DatoCMS global values */}
        {siteMetadata.favicons &&
          siteMetadata.favicons.map((d, idx) => {
            if (d.attributes) {
              return <link key={idx} {...d.attributes} />;
            }
          })}
        {/* General tags */}
        <meta property="og:site_name" content="Hanno" />
        <meta property="og:type" content="website" />
        {/* Fallback social sharing image */}
        <meta
          property="og:image"
          content={siteMetadata.dato.fallbackSeo.image.url}
        />
        <meta
          property="og:image:width"
          content={siteMetadata.dato.fallbackSeo.image.width}
        />
        <meta
          property="og:image:height"
          content={siteMetadata.dato.fallbackSeo.image.height}
        />
        {/* Fallback Twitter properties */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content={siteMetadata.dato.twitterAccount} />
        <meta
          name="twitter:image"
          content={siteMetadata.dato.fallbackSeo.image.url}
        />
      </Helmet>

      {renderMetaObjectAsTags()}

      <script type="application/ld+json">{`
          {
            "@context": "http://schema.org",
            "@type": "Organization",
            "legalName": "${siteMetadata.gatsby.legalName}",
            "name": "Hanno",
            "foundingDate": "2012-01-06",
            "foundingLocation": "London, United Kingdom",
            "taxID": "07899475",
            "url": "${siteMetadata.gatsby.siteUrl}",
            "address": "${siteMetadata.gatsby.legalAddress}",
          }
        `}</script>
    </React.Fragment>
  );
}

PageMeta.propTypes = {
  path: PathType,
  pageMeta: PageMetaType,
};
